const DEVICE_ID_STORAGE_KEY = 'sa_device_id'
const DEVICE_NAME_MAX_LENGTH = 200

/**
 * Returns a stable device id for this browser/device.
 * Uses localStorage + crypto.randomUUID(); no user permissions required.
 * User can clear storage to reset.
 */
export function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined' || !window.localStorage) {
    return fallbackRandomId()
  }
  try {
    let id = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    if (id) return id
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : fallbackRandomId()
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
    return id
  } catch {
    return fallbackRandomId()
  }
}

function fallbackRandomId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Returns a short device/browser label using only public navigator properties.
 * No permissions required; safe for privacy (no fingerprinting beyond UA).
 */
export function getDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Web'
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const browser = getBrowserName(ua)
  const os = getOsName(ua, platform)
  const name = [browser, os].filter(Boolean).join(' on ') || 'Web'
  return name.slice(0, DEVICE_NAME_MAX_LENGTH)
}

function getBrowserName(ua: string): string {
  if (/Edg\//i.test(ua)) return 'Edge'
  if (/Chrome\//i.test(ua) && !/Edg/i.test(ua)) return 'Chrome'
  if (/Firefox\//i.test(ua)) return 'Firefox'
  if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari'
  if (/OPR\//i.test(ua)) return 'Opera'
  return 'Web'
}

function getOsName(ua: string, platform: string): string {
  if (/Windows/i.test(ua) || /Win32|Win64/i.test(platform)) return 'Windows'
  if (/Mac OS X|Macintosh/i.test(ua) || /MacIntel/i.test(platform)) return 'Mac'
  if (/Linux/i.test(ua) || /Linux/i.test(platform)) return 'Linux'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad/i.test(ua)) return 'iOS'
  return platform || 'Unknown'
}
